import lottie from 'lottie-web';
import { loadFonts } from '@loaders/FontLoader';
import { enablePointerEvents, disablePointerEvents } from '@utils/pointer-events-control';

export default class LoadScene extends Phaser.Scene {
  constructor() {
    super({ key: 'LoadScene' });
  }

  async preload() {
    this.load.json('loading-anim', 'assets/loadAnims/cuemath_loading.json');
    await loadFonts();
  }

  create() {
    enablePointerEvents();

    this.loadingAnim = this.addLoaderLottie(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2,
    );

    this.assetsLoad();
    this.load.start();

    this.load.on(
      'progress',
      () => {
        this.loadingAnim.anim.playSegments([0, 25], true);
      },
      this,
    );

    this.load.on(
      'complete',
      () => {
        this.loadingAnim.anim.loop = false;
        this.loadingAnim.anim.playSegments([26, 41], true);
        this.loadingAnim.anim.addEventListener('complete', () => {
          disablePointerEvents();
          this.scene.stop('LoadScene');
          this.scene.start('StartScene');
        });
      },
      this,
    );
  }

  addLoaderLottie(_x, _y) {
    const lottieRef = this.add
      .dom(_x - this.cameras.main.width / 2, _y - this.cameras.main.height / 2, 'div')
      .setScale(0.7);

    const lottieDiv = lottieRef.node;
    lottieDiv.style.height = '100%';
    lottieDiv.style.width = '100%';

    const lottieAnim = lottie.loadAnimation({
      container: lottieDiv,
      renderer: 'svg',
      loop: true,
      autoplay: false,
      animationData: this.cache.json.get('loading-anim'),
    });

    return { anim: lottieAnim, dom: lottieRef };
  }

  assetsLoad() {
    // Audio
    this.load.audio('bgaudio', 'assets/Audio/bgm/bgm1.mp3');
    this.load.audio('correctAnswer', 'assets/Audio/correctAnswer.mp3');
    this.load.audio('wrongAnswer', 'assets/Audio/wrongAnswer.wav');
    this.load.audio('rewardReveal', 'assets/Audio/reward reveal.mp3');
    this.load.audio('watering', 'assets/Audio/watering.wav');
    this.load.audio('sunShining', 'assets/Audio/sunShining.mp3');
    this.load.audio('flowerBlossoming', 'assets/Audio/flowerBlossoming.wav');
    this.load.audio('hover', 'assets/Audio/hover.wav');
    this.load.audio('hintAlert', 'assets/Audio/hintAlert.mp3');
    this.load.audio('yay', 'assets/Audio/yay.wav');
    this.load.audio('ta1', 'assets/Audio/vo/ta1.mp3');
    this.load.audio('ta2', 'assets/Audio/vo/ta2.mp3');
    this.load.audio('ta3', 'assets/Audio/vo/ta3.mp3');
    this.load.audio('ta4', 'assets/Audio/vo/ta4.mp3');

    // Start / Game scene assets
    this.load.multiatlas('sunAnim', 'assets/sunAnim/sunAnim.json', 'assets/sunAnim');

    this.load.atlas(
      'plant',
      'assets/StartSceneAssets/plantanim/playSwayAnim.png',
      'assets/StartSceneAssets/plantanim/playSwayAnim.json',
    );
    this.load.atlas(
      'hintFinalAnim',
      'assets/GameSceneAssets/hintFinalAnim.png',
      'assets/GameSceneAssets/hintFinalAnim.json',
    );
    this.load.image('hintClose', 'assets/GameSceneAssets/closeHintIconhdpi.png');
    this.load.image('hintText', 'assets/GameSceneAssets/hintText.png');

    this.load.image('play_button', 'assets/StartSceneAssets/play_button.png');
    this.load.image('play_button_on', 'assets/StartSceneAssets/play_button_on.png');
    this.load.image('sbg', 'assets/StartSceneAssets/sbg.png');

    this.load.image('overlay', 'assets/GameSceneAssets/overlay.png');
    this.load.image('bg', 'assets/GameSceneAssets/bg.png');

    this.load.atlas(
      'sprinkleWaterAnim',
      'assets/GameSceneAssets/Sprinkle water anim/sprinkleWaterAnim.png',
      'assets/GameSceneAssets/Sprinkle water anim/sprinkleWaterAnim.json',
    );

    this.load.image('Lboard', 'assets/GameSceneAssets/Lboard.png');
    this.load.image('Rboard', 'assets/GameSceneAssets/Rboard.png');
    this.load.image('pad', 'assets/GameSceneAssets/pad.png');
    this.load.image('Lbar', 'assets/GameSceneAssets/Lbar.png');
    this.load.image('Rbar', 'assets/GameSceneAssets/Rbar.png');
    this.load.image('Rbar_w', 'assets/GameSceneAssets/Rbar_w.png');
    this.load.image('Rbox', 'assets/GameSceneAssets/Rbox.png');
    this.load.image('Rbox_w', 'assets/GameSceneAssets/Rbox_w.png');
    this.load.image('Rstick_w', 'assets/GameSceneAssets/Rstick_w.png');
    this.load.image('Rstick', 'assets/GameSceneAssets/Rstick.png');
    this.load.image('slot', 'assets/GameSceneAssets/slot.png');
    this.load.image('slot_w', 'assets/GameSceneAssets/slot_w.png');
    this.load.image('slot_c', 'assets/GameSceneAssets/slot_c.png');
    this.load.image('green', 'assets/GameSceneAssets/green.png');
    this.load.image('flower', 'assets/GameSceneAssets/flower.png');
    this.load.image('lightpanel', 'assets/GameSceneAssets/lightpanel.png');
    this.load.image('flower2', 'assets/GameSceneAssets/flower2.png');
    this.load.image('Ttext1', 'assets/GameSceneAssets/Ttext1.png');
    this.load.image('Ttext2', 'assets/GameSceneAssets/Ttext2.png');
    this.load.image('Ttext3', 'assets/GameSceneAssets/Ttext3.png');
    this.load.image('Ttext4', 'assets/GameSceneAssets/Ttext4.png');
    this.load.image('wspring', 'assets/GameSceneAssets/wspring.png');
    this.load.image('parrow', 'assets/GameSceneAssets/parrow.png');
    this.load.image('carrow', 'assets/GameSceneAssets/carrow.png');
    this.load.image('check_button', 'assets/GameSceneAssets/check_button.png');
    this.load.image('check_h', 'assets/GameSceneAssets/check_h.png');

    for (let i = 0; i < 11; i++) {
      this.load.image(i + '_', 'assets/GameSceneAssets/' + i + '_.png');
      this.load.image(i + '_h', 'assets/GameSceneAssets/' + i + '_h.png');
      this.load.image(i + '_s', 'assets/GameSceneAssets/' + i + '_s.png');
    }
  }

  update() {}
}
